/**
 * A value that only changes once the user has stopped changing it.
 *
 * Needed because provider search is a **rate-limited upstream**, not a local filter: Goong's free tier
 * allows 5 requests/second and every search costs an autocomplete plus one detail call per candidate, so
 * a request per keystroke would exhaust the ceiling while the user is still typing a word. Measured
 * advice for that API is the same: debounce the input, or a search field spends the whole pool.
 *
 * Deliberately not React Query's `staleTime`, which deduplicates identical keys but still issues one
 * request per distinct prefix.
 */

import { useEffect, useState } from "react";

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    // Clearing on every change is what makes it a debounce rather than a throttle: the timer restarts
    // while the user is typing, so nothing is asked until they pause.
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
